import React from 'react';
import { Container, Row } from 'react-bootstrap';

const EntityFiscalDistressOverview = () => {
  return (
    <section className="fiscal-distress-dashboard-overview">
      <Row className="rounded mx-1">
        <Container className="py-4">
          <h2 className="text-center h1">Overview</h2>
          <hr />
          <h3 className="text-uppercase mb-1">Introduction</h3>
          <p>
            When individuals eat healthily and exercise regularly, it is reflected in health indicators (cholesterol, blood pressure, blood sugar, etc.). People are more likely to live long, healthy lives when they monitor and maintain their health via these indicators.
          </p>
          <p>
            Governments are no different: when expenses are less than revenues, cash reserves preserved, debt service reasonable, and capital assets maintained, entities are less likely to experience a financial emergency.
          </p>
          <p>
            Defining fiscal health for local governments can be complicated. Defining potential fiscal distress is less complex. With different constituencies and services offered, local governments are as individualized and varied as people. But, just as medical tests provide insights into the health of human beings (who come in all shapes and sizes), an entity can learn to prevent potential fiscal distress by using the Fiscal Distress Dashboard.
          </p>

          <h3 className="text-uppercase mb-1">Purpose</h3>
          <ol>
            <li>Warn of potential fiscal distress by monitoring select fiscal indicators (ratios)</li>
            <li>Prompt further investigation into fiscal distress triggers.</li>
            <li>Facilitate early and informed dialogue between public officials and administrators regarding their entity's financial condition.</li>
          </ol>

          <h3 className="text-uppercase mb-1">Fiscal Distress & Fiscal Indicators</h3>
          <p>
            Financial distress is the inability of an entity to maintain existing service levels, withstand local and regional economic disruptions, and meet the demands of growth or decline.
          </p>

          <p>
            An entity's fiscal distress is measured via four fiscal areas. The Fiscal Distress Dashboard monitors these four fiscal areas via six fiscal indicators (ratios) calculated from data provided in the annual financial report.
          </p>

          <ol>
            <li>
              <b>Budgetary Solvency</b> - ability to cover expenditures with revenues and other resources over the normal budget period.
              <ul>
                <li>
                  a. Indicator 1 [Unassigned General Fund Balance/General Fund Expenditures]
                </li>
                <li>
                  b. Indicator 2 [Governmental Fund Balance/Governmental Fund Expenditures]
                </li>
                <li>
                  c. Indicator 3 [Governmental Fund Revenues/Governmental Fund Expenditures]
                </li>
              </ul>
            </li>
            <li>
              <b>Cash Solvency</b> - ability to pay the bills over the next few months.
              <ul>
                <li>
                  a. Indicator 4 [Cash & Cash Equivalents/Governmental Fund Expenditures]
                </li>
              </ul>
            </li>
            <li>
              <b>Long-Run Solvency</b> - ability to meet expenditures as they come due in the future
              <ul>
                <li>
                  a. Indicator 5 [Debt Service/Governmental Fund Expenditures
                </li>
              </ul>
            </li>
            <li>
              <b>Service Level Solvency</b> - ability to provide services at the level and quality that are required for the health, safety, and welfare of the community and that the citizens desire and expect.
              <ul>
                <li>
                  a. Indicator 6 [Accumulated Depreciation/Depreciable Capital Assets]
                </li>
              </ul>
            </li>
          </ol>

          <p>
            The Dashboard enables entity management and governing bodies to identify signs of potential future or current fiscal distress. It tests indicator benchmarks over the most recent three fiscal years. If a fiscal indicator does not achieve an established benchmark, its color will change to yellow (cautionary outlook) or red (critical outlook) - both of which should prompt further investigation. Otherwise, the indicator will remain green (satisfactory outlook). Satisfactory outlook does not necessarily denote entity overall fiscal health, only the absence of a specifically monitored potential fiscal distress trigger.
          </p>
          <hr />

          <h3 className="text-uppercase mb-1">Methodology</h3>
          <p>
            Fiscal indicators and respective benchmarks were finalized by evaluating currently available data and evaluating indicators:
          </p>

          <ol>
            <li>
              Used by other states;
            </li>
            <li>
              Published by the Government Finance Officers Association (GFOA) and International City/County Management Association (ICMA); and
            </li>
            <li>
              Recommended by a roundtable of government finance experts consisting of local government, state government, and university personnel.
            </li>
          </ol>

          <h3 className="text-uppercase mb-1">Limitations</h3>
          <h4 className="text-uppercase mb-1 fw-lighter h5">False Positives</h4>
          <p>
           The Fiscal Distress Dashboard fiscal indicators are designed to signal potential fiscal distress. Not all indictor benchmark changes indicate cause for concern. A cautionary outlook for Indicator 1 may simply arise as a result of a large portion of general fund balance being classified as committed or assigned (as opposed to unassigned). This classification decision may have been made due to prudent planning by the governing body and management and may not be indicative of potential fiscal distress. The Dashboard is designed to prompt further investigation into fiscal distress triggers and facilitate dialogue between officials and administrators. 
          </p>

          <h4 className="text-uppercase mb-1 fw-lighter h5">Governing Body & Management</h4>
          <p>
            Fiscal indicator results do not necessarily demonstrate professional competency or good governance. Additionally, appropriate policies (accounting, budgetary, capital facility, cash depositing, debt, expenditure, revenue, reporting, etc.) and proper internal controls (see the Fraud Risk Assessment) can significantly affect an entity's current and future solvency.
          </p>

          <h4 className="text-uppercase mb-1 fw-lighter h5">Incorrect & Missing Data</h4>
          <p>
            Incorrect financial report data, incorrect digitization of select financial report data, delinquent annual financial reports, and nonexistent accounting funds (e.g. a special service district which only operates an enterprise accounting fund) may invalidate some indicator results.
          </p>

          <h4 className="text-uppercase mb-1 fw-lighter h5">Timeframe</h4>
          <p>
            At a minimum, five years of data are necessary for effective trend analysis. Economic shocks (demand, supply, policy, financial, and technological) can have profound macroeconomic effects, and, as a result, drastically affect local government financial condition. These effects are not necessarily captured in the three year period used. The three year time period is used due to the limited availability of digitized financial report data.
          </p>

          <h4 className="text-uppercase mb-1 fw-lighter h5">Other Data</h4>
          <p>
            Other data affect an entity's current or future fiscal distress. These data may include: citizen demographics (education, household size, income/debt level, etc.) crime, economic (inflation, interest rates, unemployment, etc.), market (business starts, stock market volatility index, interest rate spread, etc.), housing (affordability, building permits issued, etc.), human health, internet infrastructure, tax rates, etc. The Fiscal Distress Dashboard does not monitor any of these data, although some of these data may ultimately affect the results of some indicators.
          </p>

          <h4 className="text-uppercase mb-1 fw-lighter h5">Uncomprehensive Indicators</h4>
          <p>
            "Financial condition analysis is a critical task for public managers, but it is still unclear which [fiscal] indicators are the most salient measures of financial well-being." ¹ The Fiscal Distress Dashboard fiscal indicators are neither comprehensive nor replacements for other indicators already monitored by entity management. For example, ICMA publishes over 40 financial indicators. The ratios selected were vetted by local government, state government, and university personnel, and are easily calculated from available digitized financial report data.
          </p>

          <small>
            ¹ Stone, Samuel B., Akheil Singla, James Comeaux, and Charlotte Kirschner. "A comparison of financial indicators: The case of Detroit." Public Budgeting & Finance 35, no. 4 (2015): 90-111.
          </small>
        </Container>
      </Row>
    </section>
  )
}

export default EntityFiscalDistressOverview;